import React, { useState, useEffect, useCallback, useRef } from 'react';
import { studyAPI } from '../api/study';
import './CatalogTree.css';

function buildSearchFilter(results) {
  const poemIds = new Set();
  const unitIds = new Set();
  const gradeIds = new Set();
  const textbookIds = new Set();

  results.forEach(item => {
    if (item.id) poemIds.add(item.id);
    if (item.unitId) unitIds.add(item.unitId);
    if (item.gradeId) gradeIds.add(item.gradeId);
    if (item.textbookId) textbookIds.add(item.textbookId);
  });

  return { poemIds, unitIds, gradeIds, textbookIds };
}

function CatalogTree({ libraryPoemIds = new Set(), onAdded }) {
  const [textbooks, setTextbooks] = useState([]);
  const [treeData, setTreeData] = useState({});
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchFilter, setSearchFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const searchTimerRef = useRef(null);
  const treeDataRef = useRef({});

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const data = await studyAPI.getCatalogTree();
        setTextbooks(Array.isArray(data?.textbooks) ? data.textbooks : []);
      } catch (error) {
        console.error('加载教材目录失败', error);
        setTextbooks([]);
      }
    };
    loadCatalog();
  }, []);

  const loadNodeChildren = useCallback(async (nodeType, nodeId) => {
    const cacheKey = `${nodeType}-${nodeId}`;
    if (treeDataRef.current[cacheKey]) {
      return treeDataRef.current[cacheKey];
    }

    let data;
    switch (nodeType) {
      case 'textbook':
        data = await studyAPI.getGradesByTextbook(nodeId);
        break;
      case 'grade':
        data = await studyAPI.getUnitsByGrade(nodeId);
        break;
      case 'unit':
        data = await studyAPI.getPoemsByUnit(nodeId);
        break;
      default:
        return [];
    }
    data = Array.isArray(data) ? data : [];
    treeDataRef.current = { ...treeDataRef.current, [cacheKey]: data };
    setTreeData({ ...treeDataRef.current });
    return data;
  }, []);

  const prefetchGradePoems = useCallback(async (gradeId) => {
    const units = await loadNodeChildren('grade', gradeId);
    await Promise.all(units.map(unit => loadNodeChildren('unit', unit.id)));
  }, [loadNodeChildren]);

  const prefetchTextbookPoems = useCallback(async (textbookId) => {
    const grades = await loadNodeChildren('textbook', textbookId);
    await Promise.all(grades.map(grade => prefetchGradePoems(grade.id)));
  }, [loadNodeChildren, prefetchGradePoems]);

  const expandPath = useCallback(async (textbookId, gradeId, unitId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      next.add(`textbook-${textbookId}`);
      next.add(`grade-${gradeId}`);
      next.add(`unit-${unitId}`);
      return next;
    });

    await loadNodeChildren('textbook', textbookId);
    await prefetchGradePoems(gradeId);
  }, [loadNodeChildren, prefetchGradePoems]);

  const getUnitPoems = useCallback((unitId) => {
    return treeDataRef.current[`unit-${unitId}`] || treeData[`unit-${unitId}`] || null;
  }, [treeData]);

  const getBranchPoemIds = useCallback((nodeType, nodeId) => {
    const data = treeDataRef.current;

    if (nodeType === 'unit') {
      const poems = data[`unit-${nodeId}`];
      return poems ? poems.map(p => p.id) : null;
    }

    if (nodeType === 'grade') {
      const units = data[`grade-${nodeId}`];
      if (!units) return null;
      const ids = [];
      for (const unit of units) {
        const poems = data[`unit-${unit.id}`];
        if (!poems) return null;
        ids.push(...poems.map(p => p.id));
      }
      return ids;
    }

    if (nodeType === 'textbook') {
      const grades = data[`textbook-${nodeId}`];
      if (!grades) return null;
      const ids = [];
      for (const grade of grades) {
        const units = data[`grade-${grade.id}`];
        if (!units) return null;
        for (const unit of units) {
          const poems = data[`unit-${unit.id}`];
          if (!poems) return null;
          ids.push(...poems.map(p => p.id));
        }
      }
      return ids;
    }

    return null;
  }, []);

  const areAllBranchPoemsInLibrary = useCallback((nodeType, nodeId) => {
    const poemIds = getBranchPoemIds(nodeType, nodeId);
    if (!poemIds || poemIds.length === 0) return false;
    return poemIds.every(id => libraryPoemIds.has(id));
  }, [getBranchPoemIds, libraryPoemIds]);

  const collectPoemsFromUnit = useCallback(async (unitId) => {
    const poems = await loadNodeChildren('unit', unitId);
    return poems.map(p => p.id);
  }, [loadNodeChildren]);

  const collectPoemsFromGrade = useCallback(async (gradeId) => {
    await prefetchGradePoems(gradeId);
    const units = treeDataRef.current[`grade-${gradeId}`] || [];
    const ids = [];
    for (const unit of units) {
      const poemIds = await collectPoemsFromUnit(unit.id);
      ids.push(...poemIds);
    }
    return ids;
  }, [prefetchGradePoems, collectPoemsFromUnit]);

  const collectPoemsFromTextbook = useCallback(async (textbookId) => {
    await prefetchTextbookPoems(textbookId);
    const grades = treeDataRef.current[`textbook-${textbookId}`] || [];
    const ids = [];
    for (const grade of grades) {
      const gradePoemIds = await collectPoemsFromGrade(grade.id);
      ids.push(...gradePoemIds);
    }
    return ids;
  }, [prefetchTextbookPoems, collectPoemsFromGrade]);

  const handleSearch = (value) => {
    setSearchKeyword(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      setSearchFilter(null);
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await studyAPI.searchPoems(value.trim());
        const list = Array.isArray(results) ? results : [];
        setSearchResults(list);
        setSearchFilter(list.length > 0 ? buildSearchFilter(list) : {
          poemIds: new Set(),
          unitIds: new Set(),
          gradeIds: new Set(),
          textbookIds: new Set()
        });

        for (const item of list) {
          if (item.textbookId && item.gradeId && item.unitId) {
            await expandPath(item.textbookId, item.gradeId, item.unitId);
          }
        }
      } catch (error) {
        console.error('搜索古诗失败', error);
        setSearchResults([]);
        setSearchFilter({
          poemIds: new Set(),
          unitIds: new Set(),
          gradeIds: new Set(),
          textbookIds: new Set()
        });
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const toggleNode = async (nodeKey, nodeType, nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeKey)) {
      newExpanded.delete(nodeKey);
      setExpandedNodes(newExpanded);
      return;
    }
    newExpanded.add(nodeKey);
    setExpandedNodes(newExpanded);

    if (nodeType === 'textbook') {
      await prefetchTextbookPoems(nodeId);
    } else if (nodeType === 'grade') {
      await prefetchGradePoems(nodeId);
    } else {
      await loadNodeChildren(nodeType, nodeId);
    }
  };

  const handleAddPoem = async (poem) => {
    if (libraryPoemIds.has(poem.id)) return;
    setAddingId(`poem-${poem.id}`);
    try {
      await studyAPI.addToLibrary([poem.id]);
      if (onAdded) onAdded(poem);
    } catch (error) {
      console.error('加入学习库失败', error);
      alert(error.response?.data?.message || '加入学习库失败，请重试');
    } finally {
      setAddingId(null);
    }
  };

  const handleBulkAdd = async (nodeType, nodeId, nodeKey) => {
    if (areAllBranchPoemsInLibrary(nodeType, nodeId)) return;

    setAddingId(nodeKey);
    try {
      let poemIds;
      if (nodeType === 'unit') {
        poemIds = await collectPoemsFromUnit(nodeId);
      } else if (nodeType === 'grade') {
        poemIds = await collectPoemsFromGrade(nodeId);
      } else if (nodeType === 'textbook') {
        poemIds = await collectPoemsFromTextbook(nodeId);
      } else {
        return;
      }

      const newIds = poemIds.filter(id => !libraryPoemIds.has(id));
      if (newIds.length === 0) {
        alert('该范围内所有古诗已在学习库中');
        return;
      }

      const response = await studyAPI.addToLibrary(newIds);
      if (onAdded) onAdded(response);
      const addedCount = response?.addedCount ?? newIds.length;
      alert(`成功加入 ${addedCount} 首古诗到学习库`);
    } catch (error) {
      console.error('批量加入学习库失败', error);
      alert(error.response?.data?.message || '加入学习库失败，请重试');
    } finally {
      setAddingId(null);
    }
  };

  const isSearchActive = Boolean(searchFilter);

  const shouldShowNode = (nodeType, nodeId) => {
    if (nodeType === 'unit') {
      const poems = getUnitPoems(nodeId);
      if (Array.isArray(poems) && poems.length === 0) return false;
    }

    if (!isSearchActive) return true;
    switch (nodeType) {
      case 'textbook':
        return searchFilter.textbookIds.has(nodeId);
      case 'grade':
        return searchFilter.gradeIds.has(nodeId);
      case 'unit':
        return searchFilter.unitIds.has(nodeId);
      case 'poem':
        return searchFilter.poemIds.has(nodeId);
      default:
        return true;
    }
  };

  const renderBranchAddButton = (nodeType, nodeId, nodeKey) => {
    if (nodeType === 'poem') return null;

    const isAdding = addingId === nodeKey;
    const allAdded = areAllBranchPoemsInLibrary(nodeType, nodeId);
    const poemIds = getBranchPoemIds(nodeType, nodeId);
    const canShowAdded = poemIds !== null && poemIds.length > 0 && allAdded;

    return (
      <button
        type="button"
        className={`catalog-branch-add-btn ${canShowAdded ? 'added' : ''}`}
        disabled={isAdding || canShowAdded}
        onClick={(e) => {
          e.stopPropagation();
          handleBulkAdd(nodeType, nodeId, nodeKey);
        }}
      >
        {canShowAdded ? '已添加' : isAdding ? '添加中...' : '加入学习库'}
      </button>
    );
  };

  const renderPoemNode = (poem, depth) => {
    if (!shouldShowNode('poem', poem.id)) return null;

    const inLibrary = libraryPoemIds.has(poem.id);
    const isMatch = isSearchActive && searchFilter.poemIds.has(poem.id);

    return (
      <div
        key={`poem-${poem.id}`}
        className="catalog-tree-node"
        style={{ paddingLeft: depth * 16 + 8 }}
      >
        <div className={`catalog-tree-node-poem ${isMatch ? 'match-highlight' : ''} ${inLibrary ? 'in-library' : ''}`}>
          <div className="catalog-poem-info">
            <span className="catalog-poem-title">{poem.title}</span>
            <span className="catalog-poem-author">{poem.author}{poem.dynasty ? ` · ${poem.dynasty}` : ''}</span>
          </div>
          <button
            type="button"
            className={`catalog-add-btn ${inLibrary ? 'added' : ''}`}
            disabled={inLibrary || addingId === `poem-${poem.id}`}
            onClick={() => handleAddPoem(poem)}
          >
            {inLibrary ? '已添加' : addingId === `poem-${poem.id}` ? '添加中...' : '+ 加入学习库'}
          </button>
        </div>
      </div>
    );
  };

  const renderTreeNode = (node, nodeType, depth = 0) => {
    if (!shouldShowNode(nodeType, node.id)) return null;

    const nodeKey = `${nodeType}-${node.id}`;
    const isExpanded = expandedNodes.has(nodeKey);
    const isLeaf = nodeType === 'poem';
    const children = treeData[nodeKey];

    if (isLeaf) {
      return renderPoemNode(node, depth);
    }

    const childType = nodeType === 'textbook' ? 'grade' : nodeType === 'grade' ? 'unit' : 'poem';

    return (
      <div key={nodeKey} className="catalog-tree-node" style={{ paddingLeft: depth * 16 + 8 }}>
        <div className="catalog-tree-node-row">
          <div
            className="catalog-tree-node-label"
            onClick={() => toggleNode(nodeKey, nodeType, node.id)}
          >
            <span className="catalog-tree-node-arrow">{isExpanded ? '▼' : '▶'}</span>
            <span className="catalog-tree-node-name">{node.name || node.title}</span>
          </div>
          {renderBranchAddButton(nodeType, node.id, nodeKey)}
        </div>
        {isExpanded && children && (
          <div className="catalog-tree-children">
            {children.map(child => {
              if (childType === 'poem') {
                return renderPoemNode(child, depth + 1);
              }
              return renderTreeNode(child, childType, depth + 1);
            })}
          </div>
        )}
      </div>
    );
  };

  const visibleTextbooks = isSearchActive
    ? textbooks.filter(tb => searchFilter.textbookIds.has(tb.id))
    : textbooks;

  return (
    <div className="catalog-tree-panel">
      <div className="catalog-tree-header">
        <h3>📚 教材体系</h3>
        <div className="catalog-search-bar">
          <input
            type="text"
            placeholder="搜索古诗名或作者..."
            value={searchKeyword}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      {searchKeyword.trim() && (
        <div className="catalog-search-results">
          {loading ? '搜索中...' : `找到 ${searchResults.length} 首相关古诗`}
        </div>
      )}

      <div className="catalog-tree-container">
        {visibleTextbooks.length > 0
          ? visibleTextbooks.map(tb => renderTreeNode(tb, 'textbook', 0))
          : (
            <p className="catalog-tree-empty">
              {isSearchActive ? '未找到匹配的古诗' : '暂无教材数据'}
            </p>
          )}
      </div>
    </div>
  );
}

export default CatalogTree;
